import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Grid, Skeleton, Stack, Typography } from '@mui/material'

import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetImportStatusQuery, useStartImportMutation } from '../../api/import-tool'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalStoresQuery } from '../../api/stores'
import { FeatureImportForm } from '../../features/import-tool/components/FeatureImportForm/FeatureImportForm'
import { ImportStatusAlert } from '../../features/import-tool/components/ImportStatusAlert/ImportStatusAlert'
import { WeightImportForm } from '../../features/import-tool/components/WeightImportForm/WeightImportForm'
import { importStatusPollingInterval } from '../../features/import-tool/helpers/importToolHelpers'
import { ImportStatus, ImportType } from '../../features/import-tool/types/ImportTool'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'

const importMessageKeys = {
  [ImportType.Weight]: { starting: 'internal-analysts:import_tool_weights_starting', failed: 'internal-analysts:import_tool_weights_start_failed' },
  [ImportType.Features]: { starting: 'internal-analysts:import_tool_features_starting', failed: 'internal-analysts:import_tool_features_start_failed' },
}

type ImportCardProps = {
  title: string
  description: string
  type: ImportType
  status: ImportStatus
  now: number
  children: ReactNode
}

const ImportCard: FC<ImportCardProps> = ({ title, description, type, status, now, children }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={2}>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <ImportStatusAlert type={type} status={status} now={now} />
        {/* Hidden rather than unmounted, so the values survive a start request that fails. */}
        <Stack spacing={2} sx={{ display: status.updating ? 'none' : 'flex' }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          {children}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)

// While the start request is pending the stored status may not say "updating" yet, so it is shown as running from the submit time.
const withPendingImport = (status: ImportStatus | undefined, startedTimeStamp: number | undefined, isPending: boolean): ImportStatus | undefined =>
  status && isPending && startedTimeStamp ? { ...status, updating: true, started: startedTimeStamp } : status

export const ImportToolPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-import-tool'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [now, setNow] = useState(() => Date.now())

  const weightStatusQuery = useGetImportStatusQuery(ImportType.Weight, { pollingInterval: importStatusPollingInterval })
  const featureStatusQuery = useGetImportStatusQuery(ImportType.Features, { pollingInterval: importStatusPollingInterval })
  const storesQuery = useGetInternalStoresQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const [startWeightImport, weightImport] = useStartImportMutation()
  const [startFeatureImport, featureImport] = useStartImportMutation()

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), importStatusPollingInterval)
    return () => clearInterval(interval)
  }, [])

  const startImport = useCallback(
    async (type: ImportType, body: FormData) => {
      const start = type === ImportType.Weight ? startWeightImport : startFeatureImport
      dispatch(displaySnackBar({ message: t(importMessageKeys[type].starting), severity: 'info', open: true }))
      try {
        await start({ type, body }).unwrap()
        return true
      } catch (error) {
        const reason = getValidationErrorMessage(error)
        const message = t(importMessageKeys[type].failed)
        dispatch(displaySnackBar({ message: reason ? `${message}: ${reason}` : message, severity: 'error', open: true }))
        return false
      } finally {
        setNow(Date.now())
      }
    },
    [dispatch, startFeatureImport, startWeightImport, t]
  )

  const weightStatus = withPendingImport(weightStatusQuery.data, weightImport.startedTimeStamp, weightImport.isLoading)
  const featureStatus = withPendingImport(featureStatusQuery.data, featureImport.startedTimeStamp, featureImport.isLoading)
  const stores = storesQuery.data ?? []
  const markets = marketsQuery.data ?? []
  const isLoading = weightStatusQuery.isLoading || featureStatusQuery.isLoading || storesQuery.isLoading || marketsQuery.isLoading
  const isError = weightStatusQuery.isError || featureStatusQuery.isError || storesQuery.isError || marketsQuery.isError

  return (
    <Box>
      {isLoading ? (
        <Grid container spacing={2}>
          {[ImportType.Weight, ImportType.Features].map((type) => (
            <Grid item xs={12} md={6} key={type}>
              <Skeleton variant="rectangular" height={480} />
            </Grid>
          ))}
        </Grid>
      ) : isError || !weightStatus || !featureStatus ? (
        <Typography color="error.main">{t('internal-analysts:import_tool_load_failed')}</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ImportCard
              title={t('internal-analysts:import_tool_weights_title')}
              description={t('internal-analysts:import_tool_weights_description')}
              type={ImportType.Weight}
              status={weightStatus}
              now={now}
            >
              <WeightImportForm
                stores={stores}
                markets={markets}
                isSubmitting={weightImport.isLoading}
                onSubmit={(body) => startImport(ImportType.Weight, body)}
              />
            </ImportCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ImportCard
              title={t('internal-analysts:import_tool_features_title')}
              description={t('internal-analysts:import_tool_features_description')}
              type={ImportType.Features}
              status={featureStatus}
              now={now}
            >
              <FeatureImportForm
                stores={stores}
                markets={markets}
                isSubmitting={featureImport.isLoading}
                onSubmit={(body) => startImport(ImportType.Features, body)}
              />
            </ImportCard>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}
